/**
 * PDF Text Extractor
 * Uses pdfjs-dist to extract text content from gzip-compressed base64 PDF data
 * stored in localStorage (dms_documents.file_base64).
 */

import * as pdfjsLib from 'pdfjs-dist';
import { preprocessCanvasForOcr, type PreprocessOpts } from './ocrPreprocess';

// Configure worker — use local copy in /public
pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;

/**
 * Decompress gzip base64 → raw Uint8Array (PDF bytes)
 */
async function decompressGzipBase64(base64: string): Promise<Uint8Array> {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);

  const DS = (window as any).DecompressionStream;
  if (typeof DS === 'undefined') {
    // Browser doesn't support DecompressionStream — assume raw PDF
    return bytes;
  }

  const ds = new DS('gzip');
  const writer = ds.writable.getWriter();
  writer.write(bytes);
  writer.close();

  const reader = ds.readable.getReader();
  const chunks: Uint8Array[] = [];

  while (true) {
    const { done, value }: { done: boolean; value: Uint8Array } = await reader.read();
    if (done) break;
    chunks.push(value);
  }

  const totalLen = chunks.reduce((s, c) => s + c.length, 0);
  const result = new Uint8Array(totalLen);
  let off = 0;
  for (const c of chunks) {
    result.set(c, off);
    off += c.length;
  }
  return result;
}

/**
 * Extract all text from a PDF stored as gzip-compressed base64.
 * Returns the full text content with pages separated by double-newlines.
 */
export async function extractTextFromPdf(base64: string): Promise<string> {
  try {
    const pdfBytes = await decompressGzipBase64(base64);

    const pdf = await pdfjsLib.getDocument({ data: pdfBytes.slice() }).promise;
    const pageTexts: string[] = [];

    // Extract text from all pages (limit to first 5 pages for performance)
    const maxPages = Math.min(pdf.numPages, 5);
    for (let i = 1; i <= maxPages; i++) {
      const page = await pdf.getPage(i);
      const content = await page.getTextContent();
      const text = content.items
        .map((item: any) => item.str)
        .join(' ');
      pageTexts.push(text);
    }

    return pageTexts.join('\n\n');
  } catch (err) {
    console.warn('[pdfTextExtractor] Failed to extract text:', err);
    return '';
  }
}

// Tesseract.js loads its worker + core (wasm) + language data from a CDN by
// default. For go-live, drop self-hosted copies under /public and point these
// env vars at them (e.g. REACT_APP_TESSERACT_LANG_PATH=/tesseract) so the app
// doesn't depend on a third-party CDN and works offline / behind a firewall.
function tesseractOptions(): Record<string, string> {
  const o: Record<string, string> = {};
  if (process.env.REACT_APP_TESSERACT_WORKER_PATH) o.workerPath = process.env.REACT_APP_TESSERACT_WORKER_PATH;
  if (process.env.REACT_APP_TESSERACT_CORE_PATH) o.corePath = process.env.REACT_APP_TESSERACT_CORE_PATH;
  if (process.env.REACT_APP_TESSERACT_LANG_PATH) o.langPath = process.env.REACT_APP_TESSERACT_LANG_PATH;
  return o;
}

export interface OcrPagesOpts {
  /** how many pages to OCR (default 3) */
  maxPages?: number;
  /** OCR only the top fraction of each page — 0.3 = top 30% (default 1 = whole page) */
  topFraction?: number;
  /**
   * Clean each rendered page before OCR (grayscale → normalize → Otsu binarize)
   * to cut noise on scans/photos. `true` = defaults, a PreprocessOpts to tune, or
   * `false` to skip (e.g. a clean digital render). Default `true`.
   */
  preprocess?: boolean | PreprocessOpts;
}

/**
 * Render PDF pages to a canvas and OCR them with Tesseract.js — FREE, fully
 * client-side, no API cost. Used as a fallback when the PDF has no text layer
 * (a scan/photo), or to read a logo/letterhead the text layer can't see (pass a
 * small topFraction to OCR just the header). tesseract.js is lazy-imported so it
 * only downloads when OCR is actually needed. Best-effort: returns '' on failure.
 */
export async function ocrPdfPages(base64: string, opts: OcrPagesOpts = {}): Promise<string> {
  const { maxPages = 3, topFraction = 1, preprocess = true } = opts;
  try {
    const pdfBytes = await decompressGzipBase64(base64);
    const pdf = await pdfjsLib.getDocument({ data: pdfBytes.slice() }).promise;
    const Tesseract = (await import('tesseract.js')).default;
    const tOpts = tesseractOptions();

    const n = Math.min(pdf.numPages, maxPages);
    const parts: string[] = [];
    // OCR accuracy is dominated by render resolution. A fixed scale starves a
    // small media box (e.g. a thermal-receipt PDF ~157pt wide → only ~315px at
    // scale 2). Scale each page toward ~1600px wide instead — never below 2,
    // capped at 10 to bound canvas memory on large pages.
    const TARGET_W = 1600;
    for (let i = 1; i <= n; i++) {
      const page = await pdf.getPage(i);
      const base = page.getViewport({ scale: 1 });
      const scale = Math.max(2, Math.min(10, TARGET_W / base.width));
      const viewport = page.getViewport({ scale });
      const canvas = document.createElement('canvas');
      canvas.width = viewport.width;
      // A shorter canvas clips the page to its top — cheap way to OCR just the header.
      canvas.height = topFraction < 1
        ? Math.max(1, Math.round(viewport.height * topFraction))
        : viewport.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) continue;
      await page.render({ canvasContext: ctx, viewport } as any).promise;
      if (preprocess !== false) {
        preprocessCanvasForOcr(canvas, typeof preprocess === 'object' ? preprocess : {});
      }
      const { data } = await Tesseract.recognize(canvas as any, 'eng', tOpts);
      parts.push(data?.text || '');
    }
    return parts.join('\n').trim();
  } catch (err) {
    console.warn('[pdfTextExtractor] OCR failed:', err);
    return '';
  }
}
